import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { updateUserProfile, deleteUserAccount } from '../api/profileApi';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { fetchData } from '../utils/apiHelpers';
import { getAll } from '../api';
import { HIERARCHY_STRUCTURE_DROPDOWN_API } from '../constants/hierarchy';

import { generateStrongPassword } from '../utils/passwordUtils';
import { useValidator } from '../utils/useValidator';

const ViewProfileLayer = () => {
  const userProfile = useUserProfile();
  const [profileData, setProfileData] = useState(null);
  const [originalProfileData, setOriginalProfileData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteForm, setDeleteForm] = useState({ confirm: '' });
  const deleteValidation = [{ field: 'confirm', label: 'Confirmation', required: true }];
  const {
    errors: deleteErrors,
    setErrors: setDeleteErrors,
    validate: validateDelete,
  } = useValidator(deleteValidation, deleteForm);
  const [departments, setDepartments] = useState([]);
  const maxFileSize = 5 * 1024 * 1024; // 5MB

  // Load profile data from context
  useEffect(() => {
    if (userProfile) {
      setProfileData(userProfile);
      setOriginalProfileData(userProfile);
    }
  }, [userProfile]);

  useEffect(() => {
    fetchData(getAll, { apiName: HIERARCHY_STRUCTURE_DROPDOWN_API }, setDepartments);
  }, []);

  // Upload a new profile image
  const handleImageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > maxFileSize) {
        alert('File is too large. Maximum allowed size is 5MB.');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/upload/userimages`,
          {
            method: 'POST',
            body: formData,
            credentials: 'include',
          },
        );
        const data = await res.json();
        setProfileData((prev) => ({ ...prev, user_image: data.key }));
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setProfileData({ ...profileData, [id]: value });
  };

  // Toggle edit mode
  const toggleEdit = () => {
    setEditMode(true);
  };

  // Reset to last saved values
  const handleReset = () => {
    setProfileData(originalProfileData);
    setEditMode(false);
  };

  // Save changes to backend
  const handleSave = (e) => {
    e.preventDefault();
    const payload = {
      name: profileData.name,
      email: profileData.email,
      user_phone: profileData.user_phone,
      user_bio: profileData.user_bio,
      user_image: profileData.user_image,
    };
    updateUserProfile(payload)
      .then((updatedProfile) => {
        setProfileData(updatedProfile);
        setOriginalProfileData(updatedProfile);
        setEditMode(false);
      })
      .catch((error) => console.error('Error updating profile:', error));
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    try {
      await updateUserProfile({ passwordHash: newPassword });
      setShowResetModal(false);
      setNewPassword('');
      setShowPassword(false);
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  const handleGeneratePassword = () => {
    const pwd = generateStrongPassword();
    setNewPassword(pwd);
    setShowPassword(true);
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (!validateDelete()) return;
    if (deleteForm.confirm !== 'DELETE') {
      setDeleteErrors({ confirm: 'You must type DELETE to confirm' });
      return;
    }
    try {
      await deleteUserAccount();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting account:', error);
    }
  };

  if (!profileData) {
    return <div>Loading...</div>;
  }

  const profileImageSrc = profileData.user_image
    ? `${process.env.REACT_APP_SPACES_BASE}/${profileData.user_image}`
    : 'assets/images/user.png';

  return (
    <div className="card h-100 p-24 radius-12">
      <div className="mb-24">
      </div>
      <form onSubmit={handleSave}>
        <div className="row mt-0">
          <div className="col-md-2">
            <div className="text-center mb-24 position-relative d-inline-block">
              {editMode && (
                <>
                  <input
                    type="file"
                    id="imageUpload"
                    accept=".png, .jpg, .jpeg"
                    hidden
                    onChange={handleImageChange}
                  />
                  <label
                    htmlFor="imageUpload"
                    className="position-absolute end-0 bottom-0 w-32-px h-32-px d-flex justify-content-center align-items-center bg-primary-50 text-primary-600 border border-primary-600 rounded-circle cursor-pointer"
                  >
                    <Icon icon="solar:camera-outline" />
                  </label>
                </>
              )}
              <img
                src={profileImageSrc}
                alt="Profile"
                className="rounded-circle"
                style={{ width: '100px', height: '100px', objectFit: 'cover' }}
              />
            </div>
          </div>
          {profileData.departments && (
            <div className="col-md-5">
              <span className="form-label mb-16">Assigned Departments</span>
              <div className="d-flex flex-wrap border radius-6 p-8" style={{ background: '#e9ecef' }}>
                {profileData.departments[0] === -1 ? (
                  <span className="text-secondary-light border radius-4 px-8 my-4 text-sm fw-medium mx-2 bg-white">
                    All Departments
                  </span>
                ) : (
                  departments
                    .filter((d) => profileData.departments.includes(d.id))
                    .map((d) => (
                      <span
                        key={d.id}
                        className="text-secondary-light border radius-4 px-8 my-4 text-sm fw-medium mx-2 bg-white"
                      >
                        {d.name}
                      </span>
                    ))
                )}
              </div>
            </div>
          )}

          {profileData.roles && profileData.roles.length > 0 && (
            <div className="col-md-5">
              <span className="form-label mb-16">Assigned Roles</span>
              <div className="d-flex flex-wrap border radius-6 p-8" style={{ background: '#e9ecef' }}>
                {profileData.roles.map((r) => (
                  <span
                    key={r.id}
                    className="text-secondary-light border radius-4 px-8 my-4 text-sm fw-medium mx-2 bg-white"
                  >
                    {r.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="mb-3">
          <label htmlFor="name" className="form-label">Name</label>
          <input
            type="text"
            className="form-control"
            id="name"
            value={profileData.name || ''}
            onChange={handleInputChange}
            disabled={!editMode}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="email" className="form-label">Email</label>
          <input
            type="email"
            className="form-control"
            id="email"
            value={profileData.email || ''}
            onChange={handleInputChange}
            disabled={true}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="user_phone" className="form-label">Phone Number</label>
          <input
            type="text"
            className="form-control"
            id="user_phone"
            value={profileData.user_phone || ''}
            onChange={handleInputChange}
            disabled={!editMode}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="user_bio" className="form-label">Bio</label>
          <textarea
            className="form-control"
            id="user_bio"
            rows="4"
            value={profileData.user_bio || ''}
            onChange={handleInputChange}
            disabled={!editMode}
          />
        </div>

        {editMode ? (
          <div className="d-flex justify-content-end gap-2 mt-24">
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete My Account
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => setShowResetModal(true)}
            >
              Reset Password
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleReset}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save
            </button>
          </div>
        ) : (
          <div className="d-flex justify-content-end gap-2 mt-24">
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete My Account
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => setShowResetModal(true)}
            >
              Reset Password
            </button>
            <button type="button" className="btn btn-primary" onClick={toggleEdit}>
              Edit Details
            </button>
          </div>
        )}
      </form>

      {showResetModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reset Password</h5>
                <button type="button" className="btn-close" onClick={() => setShowResetModal(false)} />
              </div>
              <form onSubmit={handlePasswordSave}>
                <div className="modal-body">
                  <div className="d-flex">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-control"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <button type="button" className="btn btn-outline-secondary ms-2" onClick={handleGeneratePassword}>
                      Generate
                    </button>
                    <button type="button" className="btn btn-secondary ms-2" onClick={() => setShowPassword((p) => !p)}>
                      <Icon icon={showPassword ? 'ic:baseline-visibility-off' : 'ic:baseline-visibility'} className="text-xl text-white" />
                    </button>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowResetModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Account</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <form onSubmit={handleDeleteAccount}>
                <div className="modal-body">
                  <p className="fw-bold">
                    Do you want to delete your account?
                  </p>
                  <p>
                    Data associated with your user account will be removed. This cannot be undone. 
                  </p>
                  <p>
                    Type the word DELETE to confirm.
                  </p>
                  <input
                    type="text"
                    className={`form-control${deleteErrors.confirm ? ' is-invalid' : ''}`}
                    value={deleteForm.confirm}
                    onChange={(e) => setDeleteForm({ confirm: e.target.value })}
                  />
                  {deleteErrors.confirm && (
                    <div className="invalid-feedback">{deleteErrors.confirm}</div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    OK
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewProfileLayer;
